import { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { CheckCircle2, XCircle, Info, AlertTriangle, X } from "lucide-react";

export type ToastType = "success" | "error" | "info" | "warning";

interface ToastProps {
  title?: string;
  message: string;
  type: ToastType;
  onClose: () => void;
  duration?: number;
}

export function Toast({
  title,
  message,
  type,
  onClose,
  duration = 3500,
}: ToastProps) {
  const [isVisible, setIsVisible] = useState(true);

  const handleClose = useCallback(() => {
    setIsVisible(false);
    setTimeout(onClose, 300); // Wait for fade-out animation
  }, [onClose]);

  useEffect(() => {
    const timer = setTimeout(() => {
      handleClose();
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, handleClose]);

  const getTypeStyles = () => {
    switch (type) {
      case "success":
        return {
          icon: <CheckCircle2 className="text-emerald-500" size={20} />,
          border: "border-emerald-100",
          bg: "bg-white",
        };
      case "error":
        return {
          icon: <XCircle className="text-red-500" size={20} />,
          border: "border-red-100",
          bg: "bg-white",
        };
      case "warning":
        return {
          icon: <AlertTriangle className="text-amber-500" size={20} />,
          border: "border-amber-100",
          bg: "bg-white",
        };
      case "info":
      default:
        return {
          icon: <Info className="text-blue-500" size={20} />,
          border: "border-blue-100",
          bg: "bg-white",
        };
    }
  };

  const styles = getTypeStyles();

  return createPortal(
    <div
      className={`fixed z-[110] transition-all duration-300 ease-out
                /* Desktop: Top-Right */
                sm:top-6 sm:right-6 sm:translate-x-0
                /* Mobile: Top-Center */
                top-4 left-4 right-4 sm:left-auto sm:w-[400px]
                ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-4"}`}
    >
      <div
        className={`flex gap-3 p-4 rounded-[10px] bg-white shadow-[0_8px_30px_rgb(0,0,0,0.08)] border ${styles.border} relative group`}
      >
        <div className="shrink-0 mt-0.5">{styles.icon}</div>
        <div className="flex-1 min-w-0 pr-6">
          <h4 className="text-sm font-bold text-slate-900 leading-none mb-1">
            {title}
          </h4>
          <p className="text-xs text-slate-500 leading-snug">{message}</p>
        </div>
        <button
          onClick={handleClose}
          className="absolute top-3 right-3 text-slate-300 hover:text-slate-500 transition-colors bg-slate-50 rounded-full p-1 group-hover:bg-slate-100"
          aria-label="Close notification"
        >
          <X size={14} />
        </button>
      </div>
    </div>,
    document.getElementById("h-portal-root") || document.body,
  );
}
